// The pool's surface as a height field (shallow water, after Kass & Miller
// 1990, in its simplest wave-equation form). Presentation only: the game's
// rules live in src/sim and never read this. Wakes, landings, pens and rain
// push the surface; the waves travel, reflect off the rim and rocks, and fade.
// No three.js here, so it runs in node tests.

export class HeightField {
  // `solid` marks cells that never move (outside the pool, rocks).
  constructor(w, h, solid, { speed = 0.045, damp = 0.982 } = {}) {
    this.w = w; this.hgt = h;
    this.h = new Float32Array(w * h);
    this.v = new Float32Array(w * h);
    this.solid = solid;
    this.speed = speed;   // c^2 dt^2 / dx^2; must stay well under 0.5
    this.damp = damp;
  }

  // Push the surface down (amount > 0) in a soft disc around (cx, cy), in cells.
  disturb(cx, cy, r, amount) {
    const { w, hgt } = this;
    const x0 = Math.max(0, Math.floor(cx - r)), x1 = Math.min(w - 1, Math.ceil(cx + r));
    const y0 = Math.max(0, Math.floor(cy - r)), y1 = Math.min(hgt - 1, Math.ceil(cy + r));
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      const i = x + y * w;
      if (this.solid[i]) continue;
      const d = Math.hypot(x - cx, y - cy) / r;
      if (d < 1) this.v[i] -= amount * 0.5 * (1 + Math.cos(Math.PI * d));
    }
  }

  // One fixed step. A wall cell reflects: its neighbours see their own height there.
  step() {
    const { w, hgt, h, v, solid, speed, damp } = this;
    for (let y = 0; y < hgt; y++) for (let x = 0; x < w; x++) {
      const i = x + y * w;
      if (solid[i]) continue;
      const c = h[i];
      const l = x > 0 && !solid[i - 1] ? h[i - 1] : c;
      const r = x < w - 1 && !solid[i + 1] ? h[i + 1] : c;
      const u = y > 0 && !solid[i - w] ? h[i - w] : c;
      const d = y < hgt - 1 && !solid[i + w] ? h[i + w] : c;
      // A weak pull back to rest keeps the mean level from drifting under stirring.
      v[i] = (v[i] + (l + r + u + d - 4 * c) * speed - c * 0.004) * damp;
    }
    for (let i = 0; i < h.length; i++) if (!solid[i]) h[i] += v[i];
  }

  // Bilinear height at a point in cells (0 outside).
  height(cx, cy) {
    const { w, hgt, h } = this;
    const x = Math.max(0, Math.min(w - 1.001, cx)), y = Math.max(0, Math.min(hgt - 1.001, cy));
    const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0, i = x0 + y0 * w;
    const a = h[i] + (h[i + 1] - h[i]) * fx, b = h[i + w] + (h[i + w + 1] - h[i + w]) * fx;
    return a + (b - a) * fy;
  }
  slope(cx, cy) { return [(this.height(cx + 1, cy) - this.height(cx - 1, cy)) / 2, (this.height(cx, cy + 1) - this.height(cx, cy - 1)) / 2]; }

  // Heights as bytes centred on 128 (±1 maps to the full range), one per cell.
  encode(out) {
    const { h } = this;
    for (let i = 0; i < h.length; i++) out[i] = Math.max(0, Math.min(255, Math.round(128 + h[i] * 127)));
  }
}
